import { Label } from '@/components/atoms/label';
import { formatEstimateSource } from '@/lib/constants/estimate-sales-attribution';
import { cn } from '@/lib/utils';

/**
 * The estimate's creation mechanism, read-only.
 *
 * NOT-584 (A2) — this used to be a dropdown. The system already knows how the
 * row was made (office create, proposal, job, Iris, inbound lead) and stamps it
 * with certainty, so asking a human re-asks an answered question and lets the
 * answer be wrong: nobody picks "AI" for an estimate Iris wrote, and `source`
 * is exactly what Iris's attribution metrics (NOT-562) count.
 *
 * Shown rather than hidden because where an estimate came from is worth seeing
 * — it is metadata, not an input.
 */
export function EstimateSourceValue({ source }: { source?: string | null }) {
  return (
    <>
      <p className="text-sm" data-testid="estimate-source-readout">
        {formatEstimateSource(source)}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Set automatically when the estimate is created
      </p>
    </>
  );
}

/**
 * `EstimateSourceValue` under its own label, for the sales-attribution card
 * whose sibling fields label themselves with the `Label` atom.
 *
 * The quick-fields grid does NOT use this — it wraps `EstimateSourceValue` in
 * its own `QuickField`, whose labels are uppercase, semibold and muted. Two
 * label treatments for one field is deliberate: each context keeps the
 * convention its neighbours use, rather than this component forking a third
 * that matches neither. The first version did exactly that, and "Source" landed
 * on the same row as "PO NUMBER" and "LINKED LEAD" in a different style.
 *
 * `Label asChild` borrows the sibling typography without emitting a <label>:
 * there is no control here to point at, and a <label> pointing at nothing is a
 * dead click target for anyone driving the page by keyboard or screen reader.
 */
export function EstimateSourceReadout({
  source,
  className,
}: {
  source?: string | null;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label asChild>
        <span>Source</span>
      </Label>
      <EstimateSourceValue source={source} />
    </div>
  );
}
